/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { concat } from 'lodash';
import { http, HttpResponse } from 'msw';
import type { RequestHandler } from 'msw';
import type { SigmetFromBackend, SigmetPhenomena } from '@opengeoweb/api';
import { LevelUnits, ProductStatus } from '@opengeoweb/api';
import { sessionStorageProvider } from '@opengeoweb/shared';
import airmetListJSON from './airmetList.json';
import drawingListJSON from './drawingsList.json';
import drawingsListFullContentJSON from './drawingsListFullContent.json';
import maritimeConfig from './maritimeConfig.json';
import maskListJSON from './masksList.json';
import predefinedAreaListJSON from './predefinedAreaList.json';
import sigmetListJSON from './sigmetList.json';
import warningEventsConfigJSON from './warningEventsConfig.json';
import warningEventsListJSON from './warningEventsList.json';
import warningListJSON from './warningList.json';
import maritimeListJSON from './warningListMaritime.json';
import { aviationEndpoints } from '../../api/aviation-api/fakeApi';
import type {
  DrawingFromBE,
  DrawingFromFE,
  DrawingListItem,
} from '../../store/warningsDrawings/types';
import type {
  AirmetWarning,
  MaritimeWarning,
  PublicWarning,
  SigmetWarning,
  Warning,
  WarningEvent,
  WarningEventsConfig,
} from '../../store/warningsForm/types';
import { transformWarningEventsToWarningFormat } from '../warningUtils';

sessionStorageProvider.setConfig({
  GW_AIRMET_BASE_URL: 'http://mock-aviation',
  GW_SIGMET_BASE_URL: 'http://mock-aviation',
  GW_DRAWINGS_BASE_URL: 'http://mock-drawings',
  GW_FEATURE_WARNINGS_EVENT: true,
  GW_WARNINGS_MARITIME_WARNINGS: true,
});

export const newSigmet: SigmetFromBackend = {
  uuid: '969f3a1e-4c15-11f0-9118-568ff32668f6',
  lastUpdateDate: new Date().toISOString(),
  creationDate: new Date().toISOString(),
  author: 'test@user.no',
  canbe: ['DRAFTED', 'PUBLISHED', 'DISCARDED'],
  sigmet: {
    status: ProductStatus.DRAFT,
    phenomenon: 'EMBD_TS' as SigmetPhenomena,
    type: 'NORMAL',
    firName: 'AMSTERDAM FIR',
    locationIndicatorATSR: 'EHAA',
    validDateStart: '2025-06-18T11:30:00Z',
    validDateEnd: '2025-06-18T13:30:00Z',
    locationIndicatorMWO: 'EFKL',
    locationIndicatorATSU: 'EHAA',
    uuid: '969f3a1e-4c15-11f0-9118-568ff32668f6',
    isObservationOrForecast: 'OBS',
    change: 'WKN',
    levelInfoMode: 'TOPS',
    level: {
      unit: LevelUnits.FL,
      value: 50,
    },
    firGeometry: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [5.0, 55.0],
                [4.331914, 55.332644],
                [3.368817, 55.764314],
                [2.761908, 54.379261],
                [3.15576, 52.913554],
                [2.000002, 51.500002],
                [3.370001, 51.369722],
                [3.370527, 51.36867],
                [3.362223, 51.320002],
                [3.36389, 51.313608],
                [3.373613, 51.309999],
                [3.952501, 51.214441],
                [4.397501, 51.452776],
                [5.078611, 51.391665],
                [5.848333, 51.139444],
                [5.651667, 50.824717],
                [6.011797, 50.757273],
                [5.934168, 51.036386],
                [6.222223, 51.361666],
                [5.94639, 51.811663],
                [6.405001, 51.830828],
                [7.053095, 52.237764],
                [7.031389, 52.268885],
                [7.063612, 52.346109],
                [7.065557, 52.385828],
                [7.133055, 52.888887],
                [7.14218, 52.898244],
                [7.191667, 53.3],
                [6.5, 53.666667],
                [6.500002, 55.000002],
                [5.0, 55.0],
              ],
            ],
          },
          properties: {
            selectionType: 'fir',
          },
        },
      ],
    },
    startGeometry: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [5.0, 55.0],
                [4.331914, 55.332644],
                [3.368817, 55.764314],
                [2.761908, 54.379261],
                [3.15576, 52.913554],
                [2.000002, 51.500002],
                [3.370001, 51.369722],
                [3.370527, 51.36867],
                [3.362223, 51.320002],
                [3.36389, 51.313608],
                [3.373613, 51.309999],
                [3.952501, 51.214441],
                [4.397501, 51.452776],
                [5.078611, 51.391665],
                [5.848333, 51.139444],
                [5.651667, 50.824717],
                [6.011797, 50.757273],
                [5.934168, 51.036386],
                [6.222223, 51.361666],
                [5.94639, 51.811663],
                [6.405001, 51.830828],
                [7.053095, 52.237764],
                [7.031389, 52.268885],
                [7.063612, 52.346109],
                [7.065557, 52.385828],
                [7.133055, 52.888887],
                [7.14218, 52.898244],
                [7.191667, 53.3],
                [6.5, 53.666667],
                [6.500002, 55.000002],
                [5.0, 55.0],
              ],
            ],
          },
          properties: {
            selectionType: 'fir',
            fill: '#ff7800',
            'fill-opacity': 0.2,
            stroke: '#ff7800',
            'stroke-width': 2,
            'stroke-opacity': 1,
          },
        },
      ],
    },
    startGeometryIntersect: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [2.000002, 51.500002],
                [3.370001, 51.369722],
                [3.362223, 51.320002],
                [3.36389, 51.313608],
                [3.373613, 51.309999],
                [3.952501, 51.214441],
                [4.397501, 51.452776],
                [5.078611, 51.391665],
                [5.848333, 51.139444],
                [5.651667, 50.824717],
                [6.011797, 50.757273],
                [5.934168, 51.036386],
                [6.222223, 51.361666],
                [5.94639, 51.811663],
                [6.405001, 51.830828],
                [7.053095, 52.237764],
                [7.031389, 52.268885],
                [7.063612, 52.346109],
                [7.065557, 52.385828],
                [7.133055, 52.888887],
                [7.14218, 52.898244],
                [7.191667, 53.3],
                [6.5, 53.666667],
                [6.500002, 55.000002],
                [5.0, 55.0],
                [4.331914, 55.332644],
                [3.368817, 55.764314],
                [2.761908, 54.379261],
                [3.15576, 52.913554],
                [2.000002, 51.500002],
              ],
            ],
          },
          properties: {
            stroke: '#ff7800',
            'stroke-width': 2,
            'stroke-opacity': 1,
            fill: '#ff7800',
            'fill-opacity': 0.5,
          },
        },
      ],
    },
    movementType: 'STATIONARY',
    sequence: '-1',
  },
};

const drawingList = drawingListJSON as DrawingListItem[];
const drawingsListFullContent = drawingsListFullContentJSON as DrawingFromBE[];
const maskList = maskListJSON as DrawingListItem[];
const predefinedAreaList = predefinedAreaListJSON as DrawingListItem[];
const warningList = warningListJSON.map((pw) => ({
  ...pw,
  type: 'public',
})) as PublicWarning[];
const lengthWarningListWithoutExpiredWithdrawn = warningList.filter(
  (warning) => warning.status !== 'EXPIRED' && warning.status !== 'WITHDRAWN',
).length;
const sigmetList = sigmetListJSON.map((sig) => ({
  ...sig,
  type: 'sigmet',
})) as unknown as SigmetWarning[];
const airmetList = airmetListJSON.map((air) => ({
  ...air,
  type: 'airmet',
})) as unknown as AirmetWarning[];
const maritimeList = maritimeListJSON.map((maritime) => ({
  ...maritime,
  type: maritime.warningDetail.domain,
})) as MaritimeWarning[];

const warningEventsList = warningEventsListJSON.map((event) => ({
  ...event,
  type: 'warningsEvent',
})) as WarningEvent[];

const transformedWarningEvents =
  warningEventsList?.flatMap((event) =>
    transformWarningEventsToWarningFormat(event),
  ) || [];

const combinedWarnings = concat<Warning>(
  warningList,
  sigmetList,
  airmetList,
  maritimeList,
  transformedWarningEvents,
);

const areaKeywordList = [
  'North Sea districts',
  'Coastal Districts',
  'KNMI Specific',
  'Objects',
  'Baltic areas',
  'NL',
  'FI',
  'METNorway Specific',
  'Provinces',
  'FIR',
  'Countries',
];

export const publicWarningEndpoints = [
  http.get(/^.*\/maritime-warnings-config$/, async () => {
    return HttpResponse.json(maritimeConfig);
  }),
  http.get(/^.*\/warnings$/, ({ request }) => {
    const url = new URL(request.url);
    const startTime = url.searchParams.get('start_time');
    const endTime = url.searchParams.get('end_time');

    let filtered = warningList;

    if (startTime && endTime) {
      // Filter by time range overlap
      filtered = warningList.filter((warning) => {
        const validFrom = warning.warningDetail?.validFrom;
        const validUntil = warning.warningDetail?.validUntil;

        if (!validFrom) {
          return false;
        }

        if (validUntil && validUntil < startTime) {
          return false;
        }
        if (validFrom > endTime) {
          return false;
        }

        return true;
      });

      const now = new Date().toISOString();

      filtered = filtered.map((warning) => {
        const validUntil = warning.warningDetail?.validUntil;
        const keepStatus = ['DRAFT', 'DRAFT_PUBLISHED', 'IGNORED'].includes(
          warning.status as string,
        );
        const hasExpired = validUntil && validUntil < now;

        if (hasExpired && !keepStatus) {
          return { ...warning, status: 'EXPIRED' } as PublicWarning;
        }
        return warning as PublicWarning;
      });
    }
    return HttpResponse.json(filtered);
  }),
  http.post(/^.*\/warnings\/(.*)$/, async () => {
    return HttpResponse.json(null);
  }),
];

export const maritimeWarningEndpoints = [
  http.post('*/maritime/', async () => {
    // publish
    return HttpResponse.json(null);
  }),
  http.post('*/maritime/maritimeWarning*', async () => {
    // editor update
    return HttpResponse.json(null);
  }),
];

export const drawingEndpoints = [
  http.get(/^.*\/drawings\/predefinedAreas$/, () => {
    return HttpResponse.json(predefinedAreaList);
  }),
  http.get(/^.*\/drawings$/, ({ request }) => {
    const keywords = new URL(request.url).searchParams.get('keywords');
    if (keywords !== 'mask') {
      return undefined;
    }
    return HttpResponse.json(maskList);
  }),
  http.get(/^.*\/drawings$/, ({ request }) => {
    const scope = new URL(request.url).searchParams.get('scope');
    return HttpResponse.json(
      scope
        ? drawingList.filter((drawing) => drawing.scope === scope)
        : drawingList,
    );
  }),
  // drawing details, needed by every component which needs to know the stored
  // drawing, like the drawing toolbox to detect unsaved changes
  http.get(/^.*\/drawings\/([^/]+)$/, ({ params }) => {
    const drawingId = params[0];
    const drawing = drawingsListFullContent.find(
      (fullContentDrawing) => fullContentDrawing.id === drawingId,
    );
    return drawing
      ? HttpResponse.json(drawing)
      : HttpResponse.json({ message: 'not found' }, { status: 404 });
  }),
  http.get(/^.*\/area-keywords$/, () => {
    return HttpResponse.json(areaKeywordList);
  }),
  http.post(/^.*\/drawings\/$/, async ({ request }) => {
    const body = (await request.json()) as DrawingFromFE;

    return HttpResponse.json(
      {
        id: 'new-drawing-id-123',
        location: 'new-location',
        ...body,
      },
      { status: 200 },
    );
  }),
  http.post(/^.*\/drawings\/([^/]+)\/$/, async ({ params }) => {
    const drawingId = params[0];
    const drawing = drawingsListFullContent.find(
      (drawing) => drawing.id === drawingId,
    );
    return HttpResponse.json({ drawing }, { status: 200 });
  }),
];

export const warningEventsConfig =
  warningEventsConfigJSON as unknown as WarningEventsConfig;

export const warningEventsEndpoints = [
  http.get(/^.*\/warning-events-config$/, async () => {
    return HttpResponse.json(warningEventsConfig);
  }),
  http.get(/^.*\/events$/, ({ request }) => {
    const url = new URL(request.url);
    const startTime = url.searchParams.get('start_time');
    const endTime = url.searchParams.get('end_time');

    let filtered = warningEventsList;

    if (startTime && endTime) {
      // Filter by time range overlap
      filtered = warningEventsList.filter((event) => {
        const { validFrom, validUntil } = event;

        if (!validFrom) {
          return false;
        }

        if (validUntil && validUntil < startTime) {
          return false;
        }
        if (validFrom > endTime) {
          return false;
        }

        return true;
      });
    }
    return HttpResponse.json(filtered);
  }),
  http.post(/^.*\/events\/(.*)$/, async () => {
    return HttpResponse.json(null);
  }),
];

export const makeWarningsHandler = (result: Warning[]): RequestHandler => {
  return http.get(/^.*\/warnings$/, () => {
    // respond using a mocked JSON body
    return HttpResponse.json(result);
  });
};

const requestHandlers = [
  ...publicWarningEndpoints,
  ...drawingEndpoints,
  ...warningEventsEndpoints,
  ...aviationEndpoints,
  ...maritimeWarningEndpoints,
];

export {
  drawingList,
  maskList,
  predefinedAreaList,
  drawingsListFullContent,
  warningList,
  lengthWarningListWithoutExpiredWithdrawn,
  sigmetList,
  airmetList,
  combinedWarnings,
  areaKeywordList,
  requestHandlers,
  maritimeList,
  warningEventsList,
};
